/**
 * @fias/expression-eval
 *
 * Safe expression evaluator for SDUI compute actions.
 *
 * Features:
 * - Arithmetic: +, -, *, /, %
 * - Comparison: ===, !==, >, <, >=, <=
 * - Logical: &&, ||, !
 * - Ternary: a ? b : c
 * - Property access: input.value, output.result, payload.digit
 * - Built-in functions: Math.round(), Number(), String(), etc.
 *
 * Security:
 * - No eval() or Function()
 * - Whitelist-only approach
 * - AST-based parsing
 * - Maximum complexity limits
 * - Only access to input/output/payload contexts
 *
 * @example
 * ```typescript
 * import { evaluateExpression } from '@fias/expression-eval';
 *
 * const ctx = {
 *   input: { display: '5', operator: '+' },
 *   output: {},
 *   payload: { digit: '3' }
 * };
 *
 * // Arithmetic
 * evaluateExpression('Number(input.display) + Number(payload.digit)', ctx);
 * // { success: true, value: 8 }
 *
 * // String concatenation
 * evaluateExpression('input.display + payload.digit', ctx);
 * // { success: true, value: '53' }
 *
 * // Ternary
 * evaluateExpression("input.operator === '+' ? 'add' : 'other'", ctx);
 * // { success: true, value: 'add' }
 * ```
 */
export { ALLOWED_FUNCTIONS, ALLOWED_METHODS, ALLOWED_CONTEXTS, EXPRESSION_LIMITS, } from './types';
export { Parser, ParseError, parseExpression } from './parser';
export { EvaluationError, evaluateAST } from './evaluator';
export { validateExpression, sanitizeExpression, isExpressionSafe } from './security';
import { parseExpression, ParseError } from './parser';
import { evaluateAST, EvaluationError } from './evaluator';
import { validateExpression } from './security';
/**
 * Evaluate an expression string against a context.
 *
 * This is the main entry point for expression evaluation.
 * It validates, parses, and evaluates the expression in one call.
 *
 * @param expression - The expression string to evaluate
 * @param context - The context containing input, output, and payload
 * @returns ExpressionResult with success flag and value or error
 *
 * @example
 * ```typescript
 * const result = evaluateExpression('input.x + input.y', {
 *   input: { x: 1, y: 2 },
 *   output: {},
 *   payload: {}
 * });
 * // result: { success: true, value: 3 }
 * ```
 */
export function evaluateExpression(expression, context) {
    try {
        // Validate first
        const validation = validateExpression(expression);
        if (!validation.valid) {
            return {
                success: false,
                error: validation.errors.join('; '),
            };
        }
        // Parse
        const { ast } = parseExpression(expression);
        // Evaluate
        const value = evaluateAST(ast, context);
        return { success: true, value };
    }
    catch (err) {
        if (err instanceof ParseError || err instanceof EvaluationError) {
            return { success: false, error: err.message };
        }
        return {
            success: false,
            error: err instanceof Error ? err.message : 'Unknown error',
        };
    }
}
/**
 * Validate an expression without evaluating it.
 *
 * Use this to pre-check expressions at build/save time.
 *
 * @param expression - The expression string to validate
 * @returns ValidationResult with valid flag and any errors
 *
 * @example
 * ```typescript
 * const result = validateExpressionSyntax('input.x + ');
 * // result: { valid: false, errors: ['Unexpected token: EOF'] }
 * ```
 */
export function validateExpressionSyntax(expression) {
    // First, security validation
    const securityResult = validateExpression(expression);
    if (!securityResult.valid) {
        return securityResult;
    }
    // Then, parse validation
    try {
        const { nodeCount, maxDepth } = parseExpression(expression);
        return {
            valid: true,
            errors: [],
            nodeCount,
            maxDepth,
        };
    }
    catch (err) {
        if (err instanceof ParseError) {
            return {
                valid: false,
                errors: [err.message],
            };
        }
        return {
            valid: false,
            errors: [err instanceof Error ? err.message : 'Parse error'],
        };
    }
}
/**
 * Evaluate multiple expressions and return results as a record.
 *
 * Useful for compute actions with multiple target keys.
 *
 * @param computations - Map of target key to expression
 * @param context - The context containing input, output, and payload
 * @returns Record of results keyed by target key
 *
 * @example
 * ```typescript
 * const results = evaluateComputations({
 *   total: 'input.price * input.quantity',
 *   tax: 'input.price * input.quantity * 0.1'
 * }, { input: { price: 100, quantity: 2 }, output: {}, payload: {} });
 * // results: { total: { success: true, value: 200 }, tax: { success: true, value: 20 } }
 * ```
 */
export function evaluateComputations(computations, context) {
    const results = {};
    for (const [key, expression] of Object.entries(computations)) {
        results[key] = evaluateExpression(expression, context);
    }
    return results;
}
//# sourceMappingURL=index.js.map